// Lib
import { forwardRef, useCallback, useEffect, useImperativeHandle } from 'react';
import { isEqual } from 'lodash';
import PropTypes from 'prop-types';

// Utils
import { getCellSelectionInLayer, fixSelectionBorderDisplay } from '../utils/tableCellSelectionUtils';
import { asObject } from '../../../../common/utils/immutableHelper';
import { getElementId } from '../../../../common/elements/utils/elementPropertyUtils';
import { getTableCellEditorId, getTableGridEditorId } from '../utils/tableCellEditingUtils';

const TableCellSelectionHandlers = forwardRef(function TableCellSelectionHandlersComponent(props, ref) {
    const {
        element,
        currentCellSelections,
        currentlyEditingEditorId,

        hotTableInstanceRef,
        tableOperationsRef,

        isSingleSelected,
        isResizing,

        dispatchSetCellSelection,
        dispatchFinishEditingElement,
    } = props;

    const elementId = getElementId(element);

    useEffect(() => {
        // Prevent cells from reselecting if another element is selected or if user is editing title/caption
        // this is just because on first click onto another element the table
        // rerenders before meta is cleared from app selection state

        if (!hotTableInstanceRef.current || isResizing) return;

        if (!isSingleSelected) {
            hotTableInstanceRef.current.deselectCell();
            hotTableInstanceRef.current.unlisten();
        }

        // Start listening when the table is single selected so that shortcuts work
        if (isSingleSelected) hotTableInstanceRef.current.listen();
    }, [isSingleSelected]);

    /**
     * This prevents cell selection if the table is not single selected
     */
    useEffect(() => {
        hotTableInstanceRef.current.updateSettings({
            disableVisualSelection: !isSingleSelected,
        });
    }, [isSingleSelected]);

    /**
     * If cell Selection is updated in the redux state, apply the changes to Handsontable state
     */
    useEffect(() => {
        if (!hotTableInstanceRef.current) return;

        const currentHotCellSelections = hotTableInstanceRef.current.getSelected();
        if (isEqual(asObject(currentCellSelections), currentHotCellSelections)) return;

        tableOperationsRef.current.updateHotCellSelections(currentCellSelections);
    }, [currentCellSelections]);

    /**
     * If user focuses on another editor key on the table (e.g. title/caption), deselect the current cell selection
     */
    useEffect(() => {
        if (!hotTableInstanceRef.current || !hotTableInstanceRef.current.isListening()) return;

        if (
            !currentlyEditingEditorId ||
            currentlyEditingEditorId === getTableGridEditorId(elementId) ||
            currentlyEditingEditorId === getTableCellEditorId(elementId)
        )
            return;

        dispatchSetCellSelection(elementId, null);
    }, [elementId, currentlyEditingEditorId]);

    const afterScrollHorizontally = useCallback(() => {
        fixSelectionBorderDisplay(hotTableInstanceRef.current);
    }, []);

    const afterSelection = useCallback(
        (row, column, row2, column2, _, selectionLayerLevel) => {
            fixSelectionBorderDisplay(hotTableInstanceRef.current);

            // If user was previously focusing on another editor key on the table (e.g. title/caption),
            // and then clicks on a cell, finish editing the other editor key
            if (currentlyEditingEditorId) dispatchFinishEditingElement();

            // Apply new cell selection to redux state if it has changed
            const newCellSelection = [row, column, row2, column2];

            const currentCellSelection = asObject(getCellSelectionInLayer(currentCellSelections, selectionLayerLevel));
            if (isEqual(currentCellSelection, newCellSelection)) return;

            const transactionId = tableOperationsRef.current.popTransactionIdForNextUpdateCellSelection();
            dispatchSetCellSelection(elementId, newCellSelection, selectionLayerLevel, transactionId);
        },
        [currentCellSelections, currentlyEditingEditorId],
    );

    useImperativeHandle(
        ref,
        () => ({
            afterSelection,
            afterScrollHorizontally,
        }),
        [afterSelection, afterScrollHorizontally],
    );

    return null;
});

TableCellSelectionHandlers.propTypes = {
    element: PropTypes.object,
    currentCellSelections: PropTypes.object,
    currentlyEditingEditorId: PropTypes.string,

    hotTableInstanceRef: PropTypes.oneOfType([PropTypes.func, PropTypes.object]),
    tableOperationsRef: PropTypes.oneOfType([PropTypes.func, PropTypes.object]),

    isSingleSelected: PropTypes.bool,
    isResizing: PropTypes.bool,

    dispatchSetCellSelection: PropTypes.func,
    dispatchFinishEditingElement: PropTypes.func,
};

export default TableCellSelectionHandlers;
